import React from 'react';
import {
  Box, Paper, Container, TextField, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, TableSortLabel, Stack, Divider, Tooltip, TableContainer,
  CircularProgress, Snackbar, Alert, Chip, LinearProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Refresh, RestartAlt } from '@mui/icons-material';

import Client from '../Client';
import Config from '../ClientConfig';

// ---------- helpers ----------
const ymKey = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// number formatting helpers
const fmtFixed = (n, d) => {
  const v = Number(n);
  if (!isFinite(v)) return '-';
  return v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmt0 = (n) => (n === null || n === undefined ? '-' : fmtFixed(Math.round(Number(n) || 0), 0));
const fmt2 = (n) => (n === null || n === undefined ? '-' : fmtFixed(n, 2));
const fmt3 = (n) => (n === null || n === undefined ? '-' : fmtFixed(n, 3));

const urlJoin = (...parts) =>
  parts
    .filter(Boolean)
    .map((s, i) => (i === 0 ? String(s).replace(/\/+$/,'') : String(s).replace(/^\/+|\/+$/g,'')))
    .join('/');

// API roots
const API_URL_PROD_BREAKDOWN =
  (Config.API_URL_REPORTS_MONTH_CLOSE || '/api/reports/month-close/') + 'prod-breakdown/';
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

// optional aggregated endpoint (if present)
const API_URL_PROD_COSTS_BY_LOC =
  (Config.API_URL_REPORTS_MONTH_CLOSE || '/api/reports/month-close/') + 'prod-costs-by-location/';

// robust recipe endpoint resolution
const RESOLVE_RECIPE_URL = (clientInstance) => {
  const fromConfig = Config.API_URL_PRODUCT_RECIPE;
  const fromClient = Client.API_URL_PRODUCT_RECIPE;
  const fromClientBase = clientInstance?.BASE_API_URL ? urlJoin(clientInstance.BASE_API_URL, 'productRecipe/') : null;
  return fromConfig || fromClient || fromClientBase || '/api/productRecipe/';
};

class ProdCostsByLocation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),

      // lookups
      locations: [],
      productNameMap: {},
      productOptions: [],   // finished-only (status=2, !isPurchased)
      productOpt: null,

      // filters
      month: nowYM(),

      // data
      rows: [], // list by LOCATION (summary only; details fetched on click)
      selectedKey: '', // `${location}|${YYYY-MM}`

      // details cache per location|ym -> { breakdown, producedQty, computedUnitCost, totalProductionValue, finishedProductName }
      detailsCache: {},
      recipeByProduct: {},

      // paging
      page: 0,
      rowsPerPage: -1,
      listOrder: 'asc',
      listOrderBy: null,

      // ui
      loading: false,
      errorDialog: { open: false, detail: '' },
      toastOpen: false,
      toastMsg: '',
      toastSeverity: 'success',

      // export dialog
      exportOpen: false,
      exporting: false,
      exportDone: 0,
      exportTotal: 0,
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  // ---------- util ui ----------
  openError = (detail) => this.setState({ errorDialog: { open: true, detail: detail || 'Error' } });
  closeError = () => this.setState({ errorDialog: { open: false, detail: '' } });
  showToast = (msg, severity = 'success') =>
    this.setState({ toastOpen: true, toastMsg: msg, toastSeverity: severity });
  closeToast = () => this.setState({ toastOpen: false, toastMsg: '' });

  handleListRequestSort = (property) => {
    this.setState((state) => ({
      listOrder: state.listOrderBy === property && state.listOrder === 'asc' ? 'desc' : 'asc',
      listOrderBy: property,
      page: 0,
    }));
  };

  // ---------- name helpers ----------
  getLocationName = (locId) => {
    if (!locId) return '';
    const l = (this.state.locations || []).find(ll =>
      String(ll.iref || ll.id || ll.uid) === String(locId)
    );
    return l ? (l.name || l.description || String(locId)) : String(locId);
  };

  getProductName = (id) => {
    if (!id) return '';
    const key = String(id);
    const { productNameMap, productOptions } = this.state;
    return productNameMap[key]
      || (productOptions.find(o => o.value === key)?.label)
      || key;
  };

  // ---------- bootstrap ----------
  bootstrap = async () => {
    try {
      await Promise.all([this.loadProducts(), this.loadLocations()]);
    } catch (e) {
      this.openError(String(e?.message || e));
    }
  };

  loadLocations = async () => {
    const resp = this.state.client.getData(API_URL_LOCATIONS, { status: 1 }) || {};
    const locations = Array.isArray(resp.locations) ? resp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.setState({ locations });
  };

  loadProducts = async () => {
    const prodResp = this.state.client.getData(API_URL_PRODUCTS, { status: 2, orderBy_name: true }) || {};
    const prods = Array.isArray(prodResp.products)
      ? prodResp.products
      : (Array.isArray(prodResp) ? prodResp : (Array.isArray(prodResp.results) ? prodResp.results : []));

    const productNameMap = {};
    prods.forEach(p => {
      const pid = p.iref || p.id || p.uid || p.code;
      const name = p.name || p.productName || p.description || p.code || String(pid);
      if (pid) productNameMap[String(pid)] = String(name);
    });
    
    // finished-only like ProductRecipe.js (status=2 & not purchased)
    const productOptions = prods
      .filter(p => Number(p.status) === 2 && (p.isPurchased === false || Number(p.isPurchased) === 0))
      .map(p => ({
        value: String(p.iref || p.id || p.uid || p.code),
        label: productNameMap[String(p.iref || p.id || p.uid || p.code)]
      }));

    this.setState({ productNameMap, productOptions });
  };

  // ---------- params ----------
  buildQueryParams = () => {
    const { month, productOpt } = this.state;
    if (!productOpt || !productOpt.value) return null;
    const [year, m] = String(month || nowYM()).split('-').map(s => parseInt(s, 10));
    return { year, month: m, product: productOpt.value };
  };

  // ---------- data loading (by location) ----------
  loadList = async () => {
    const params = this.buildQueryParams();
    if (!params) {
      this.showToast('Select a product first', 'warning');
      return;
    }

    this.setState({ loading: true, rows: [], selectedKey: '', page: 0, exportDone: 0, exportTotal: 0, detailsCache: {} });

    // Prefer aggregated endpoint that returns summaries per location (no breakdown).
    let rows = [];
    try {
      const data = this.state.client.getData(API_URL_PROD_COSTS_BY_LOC, params);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
      if (list && list.length) {
        rows = list.map(r => ({
          location: String(r.location),
          finishedProduct: String(params.product),
          finishedProductName: this.getProductName(params.product),
          year: Number(r.year || params.year),
          month: Number(r.month || params.month),
          producedQty: r.producedQty == null ? null : Number(r.producedQty),
          computedUnitCost: r.computedUnitCost == null ? null : Number(r.computedUnitCost),
          totalProductionValue: r.totalProductionValue == null ? null : Number(r.totalProductionValue),
          // no breakdown here; fetch on click
        }));
      }
    } catch (_) { /* ignore */ }

    // If aggregated endpoint missing, show just locations list with empty metrics; fetch on click.
    if (!rows.length) {
      const ids = (this.state.locations || []).map(l => String(l.iref || l.id || l.uid)).filter(Boolean);
      rows = ids.map(id => ({
        location: id,
        finishedProduct: String(params.product),
        finishedProductName: this.getProductName(params.product),
        year: Number(params.year),
        month: Number(params.month),
        producedQty: null,
        computedUnitCost: null,
        totalProductionValue: null,
      }));
    }

    // sort by location name
    rows.sort((a, b) => (this.getLocationName(a.location) || '').localeCompare(this.getLocationName(b.location) || ''));

    // Auto-select first row
    const selectedKey = rows.length ? `${rows[0].location}|${ymKey(rows[0].year, rows[0].month)}` : '';

    this.setState({ rows, selectedKey, loading: false }, async () => {
      await this.refreshRecipeFor(params.product);
      // If first row lacks details, lazy-load its details to populate the right panel.
      if (selectedKey) {
        const [loc, ym] = selectedKey.split('|');
        const [y, m] = ym.split('-').map(x => parseInt(x, 10));
        await this.ensureRowDetails(loc, y, m);
      }
    });
  };

  // ---------- selection helpers ----------
  setSelected = async (row) => {
    const key = `${row.location}|${ymKey(row.year, row.month)}`;
    this.setState({ selectedKey: key }, async () => {
      await this.ensureRowDetails(row.location, row.year, row.month);
    });
  };

  getSelectedRow = () => {
    const { selectedKey, rows } = this.state;
    if (!selectedKey) return null;
    const [loc, ym] = selectedKey.split('|');
    const [y, m] = ym.split('-').map(n => parseInt(n, 10));
    const base = rows.find(r => String(r.location) === String(loc) && r.year === y && r.month === m) || null;
    if (!base) return null;
    const cacheKey = `${loc}|${ym}`;
    const details = this.state.detailsCache[cacheKey] || {};
    return { ...base, ...details };
  };

  // ---------- details lazy loader ----------
  ensureRowDetails = async (locationId, year, month) => {
    const { productOpt } = this.state;
    if (!productOpt || !productOpt.value) return;
    const cacheKey = `${locationId}|${ymKey(year, month)}`;
    if (this.state.detailsCache[cacheKey]) return; // already fetched

    try {
      const resp = this.state.client.getData(API_URL_PROD_BREAKDOWN, {
        location: locationId,
        product: productOpt.value,
        year,
        month,
      });
      const list = Array.isArray(resp) ? resp : (Array.isArray(resp?.results) ? resp.results : []);
      const row = Array.isArray(list) ? list[0] : null; // server returns 0..1 when product provided
      if (row) {
        const details = {
          producedQty: row.producedQty == null ? null : Number(row.producedQty),
          computedUnitCost: row.computedUnitCost == null ? null : Number(row.computedUnitCost),
          totalProductionValue: row.totalProductionValue == null ? null : Number(row.totalProductionValue),
          breakdown: row.breakdown || [],
          finishedProductName: row.finishedProductName || this.getProductName(productOpt.value),
        };
        this.setState(prev => ({ detailsCache: { ...prev.detailsCache, [cacheKey]: details } }));
      } else {
        // cache empty details to avoid repeat calls
        this.setState(prev => ({ detailsCache: { ...prev.detailsCache, [cacheKey]: { breakdown: [] } } }));
      }
    } catch (e) {
      // cache error as empty to prevent hammering
      this.setState(prev => ({ detailsCache: { ...prev.detailsCache, [cacheKey]: { breakdown: [] } } }));
    }
  };

  // ---------- recipe loading ----------
  refreshRecipeFor = async (productId) => {
    await this.loadRecipeFor(productId, { force: true });
  };

  loadRecipeFor = async (productId, { force = false } = {}) => {
    if (!productId) return;
    const key = String(productId);
    if (!force && this.state.recipeByProduct[key]) return; // already cached
    try {
      const recipeUrl = RESOLVE_RECIPE_URL(this.state.client);
      let resp = this.state.client.getData(recipeUrl, { product: productId }) || {};
      let list = Array.isArray(resp.productRecipe)
        ? resp.productRecipe
        : (Array.isArray(resp.results) ? resp.results : (Array.isArray(resp) ? resp : []));
      if (!Array.isArray(list) || list.length === 0) {
        resp = this.state.client.getData(recipeUrl) || {};
        list = Array.isArray(resp.productRecipe)
          ? resp.productRecipe
          : (Array.isArray(resp.results) ? resp.results : (Array.isArray(resp) ? resp : []));
      }
      const recipe = list.find(x => String(x?.product?.iref || x?.product) === String(productId)) || list[0];
      const items = Array.isArray(recipe?.recipeItem_recipe)
        ? recipe.recipeItem_recipe.map(it => ({ product: String(it.product), quantity: Number(it.quantity || 0) }))
        : [];
      this.setState(prev => ({ recipeByProduct: { ...prev.recipeByProduct, [key]: items } }));
    } catch (e) {
      this.showToast(`Recipe load failed: ${String(e?.message || e)}`, 'warning');
    }
  };

  // ---------- comparison builder (cost-first, with recipe valuation) ----------
  buildComparison = (breakdown, recipeItems) => {
    const costMap = new Map(); // ingredient -> { qty, price, lineValue }
    (breakdown || []).forEach(b => {
      const ing = String(b.ingredient);
      const qty = Number(b.qty_per_unit || 0);
      const price = Number(b.price_used || 0);
      costMap.set(ing, { qty, price, lineValue: qty * price });
    });

    const recipeMap = new Map(); // ingredient -> qty (per unit)
    (recipeItems || []).forEach(r => {
      recipeMap.set(String(r.product), Number(r.quantity || 0));
    });

    const ingSet = new Set([...costMap.keys(), ...recipeMap.keys()]);
    const lines = [];
    let recipeUnitCost = 0; // Σ(recipeQty * price_used)

    ingSet.forEach(ing => {
      const c = costMap.get(ing) || { qty: 0, price: 0, lineValue: 0 };
      const rQty = recipeMap.get(ing) || 0;
      const recipeValue = rQty * c.price; // here we value using price_used
      recipeUnitCost += recipeValue;

      lines.push({
        ingredient: ing,
        costQty: c.qty,
        priceUsedDisplay: c.price,
        lineValue: c.lineValue,
        recipeQty: rQty,
        recipeValue,
      });
    });

    return { lines, recipeUnitCost };
  };

  // ---------- CSV: selected breakdown (details) ----------
  exportSelectedCsv = () => {
    const r = this.getSelectedRow();
    if (!r) {
      this.showToast('Nothing selected to export.', 'warning');
      return;
    }

    // Build the comparison lines (details)
    const recipeItems = this.state.recipeByProduct[String(r.finishedProduct)] || [];
    const { lines } = this.buildComparison(r.breakdown || [], recipeItems || []);

    if (!lines.length) {
      this.showToast('No cost lines to export for this selection.', 'info');
      return;
    }

    const out = [
      ['summary'],
      ['period', ymKey(r.year, r.month)],
      ['location', this.getLocationName(r.location) || ''],
      ['finished_product', r.finishedProductName || ''],
      [],
      ['ingredient', 'cost_qty_per_unit', 'price_used', 'total_cost', 'recipe_qty_per_unit', 'recipe_value'],
    ];

    // Export each detail line
    lines.forEach(ln => {
      out.push([
        this.getProductName(ln.ingredient),
        fmt3(ln.costQty),
        fmt2(ln.priceUsedDisplay),
        fmt2(ln.lineValue),
        fmt3(ln.recipeQty),
        fmt2(ln.recipeValue),
      ]);
    });

    const csv = "\uFEFF" + out.map(row => row.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fname = `prod-costs-by-location_${(r.finishedProductName || 'product').replace(/\s+/g,'-').toLowerCase()}_${ymKey(r.year, r.month)}.csv`;
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- CSV: list (all locations for selected product) ----------
  exportCostsCsvAllLocations = () => {
    const { rows, productOpt, month } = this.state;
    if (!rows.length) {
      this.showToast('No rows to export.', 'warning');
      return;
    }
    const out = [
      ['summary'],
      ['product', productOpt?.label || productOpt?.value || ''],
      ['month', month],
      ['rows', String(rows.length)],
      [],
      ['location', 'period', 'produced_qty', 'unit_cost', 'total_value']
    ];

    rows.forEach(r => {
      out.push([
        this.getLocationName(r.location),
        ymKey(r.year, r.month),
        fmt0(r.producedQty),
        fmt2(r.computedUnitCost),
        fmt2(r.totalProductionValue),
      ]);
    });

    const csv = "\uFEFF" + out.map(row => row.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fname = `prod-costs-by-location_ALL_${productOpt?.label || productOpt?.value || 'product'}_${month}.csv`.replace(/\s+/g,'-');
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- aggregates for list ----------
  computeListAggregates = () => {
    const { rows } = this.state;
    // Sum qty and total value, compute weighted average unit cost
    let qty = 0;
    let total = 0;
    rows.forEach(r => {
      const q = Number(r.producedQty);
      const v = Number(r.totalProductionValue);
      if (isFinite(q) && q > 0 && isFinite(v)) {
        qty += q;
        total += v;
      }
    });
    const avgCost = qty > 0 ? (total / qty) : 0;
    return { qty, avgCost, total };
  };

  // ---------- UI ----------
  renderToolbar() {
    const { month, loading, productOptions, productOpt } = this.state;

    const fieldSx = {
      '& .MuiInputBase-root': {
        height: 34,
        borderRadius: '12px',
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
        bgcolor: '#fff',
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiSelect-select': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
      },
    };

    const buttonSx = {
      height: 34,
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 750,
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: {
            xs: 0.75,
            sm: 1,
          },
          mb: 1,
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Autocomplete
            size="small"
            options={productOptions}
            value={productOpt}
            onChange={(_e, v) =>
              this.setState({ productOpt: v || null }, () => {
                if (v) this.loadList();
              })
            }
            getOptionLabel={(o) => o?.label || String(o?.value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Finished product"
                required
                sx={fieldSx}
              />
            )}
            sx={{
              minWidth: {
                xs: '100%',
                sm: 280,
                md: 340,
              },
              flex: {
                xs: '1 1 100%',
                sm: '1 1 320px',
              },
            }}
          />

          <TextField
            label="Month"
            type="month"
            value={month}
            onChange={(e) =>
              this.setState({ month: e.target.value }, () => {
                if (this.state.productOpt) this.loadList();
              })
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{
              width: {
                xs: 145,
                sm: 150,
              },
              flexShrink: 0,
              ...fieldSx,
            }}
          />

          <Tooltip title="Refresh list for this product">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  this.loadList();
                }}
                disabled={loading}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '12px',
                  border: '1px solid rgba(26, 35, 126, 0.12)',
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.06)',
                }}
              >
                {loading ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Clear product">
            <IconButton
              size="small"
              onClick={() => this.setState({ productOpt: null, rows: [], selectedKey: '' })}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '12px',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.06)',
              }}
            >
              <RestartAlt fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'block' } }} />

          <Button
            size="small"
            variant="outlined"
            onClick={() => this.setState({ exportOpen: true })}
            disabled={!this.state.rows.length}
            sx={buttonSx}
          >
            Export
          </Button>

          <Chip
            size="small"
            label={`${this.state.rows.length} locations`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: '10px',
              fontWeight: 650,
              color: '#64748b',
            }}
          />
        </Box>
      </Paper>
    );
  }

  renderList() {
    const {
      rows, page, rowsPerPage, loading, selectedKey, exporting, exportDone, exportTotal,
      listOrder, listOrderBy,
    } = this.state;
    const sortedRows = listOrderBy
      ? [...rows].sort((a, b) => {
          const getValue = (row) => listOrderBy === 'location'
            ? String(this.getLocationName(row.location) || '').toLocaleLowerCase()
            : Number(row[listOrderBy] ?? 0);
          const aValue = getValue(a);
          const bValue = getValue(b);
          if (aValue < bValue) return listOrder === 'asc' ? -1 : 1;
          if (aValue > bValue) return listOrder === 'asc' ? 1 : -1;
          return 0;
        })
      : rows;
    const start = page * rowsPerPage;
    const pageRows = rowsPerPage > 0
      ? sortedRows.slice(start, start + rowsPerPage)
      : sortedRows;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={listOrderBy === property}
        direction={listOrderBy === property ? listOrder : 'asc'}
        onClick={() => this.handleListRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );

    const { qty, avgCost, total } = this.computeListAggregates();

    const bodyCellSx = {
      py: { xs: 0.55, sm: 0.75, md: 0.8 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#111827',
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
      fontVariantNumeric: 'tabular-nums',
    };

    const headerCellSx = {
      py: { xs: 0.5, sm: 0.7, md: 0.85 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 700,
      lineHeight: 1.15,
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          position: 'relative',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          borderRadius: '14px',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 0.75, sm: 1 },
            pt: { xs: 0.75, sm: 1 },
            pb: 0.5,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
          }}
        >
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              variant="outlined"
              label={<span><strong>Qty:</strong> {fmt0(qty)}</span>}
              sx={{ height: 26, borderRadius: '10px', fontWeight: 650 }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={<span><strong>Avg UCost:</strong> {fmt2(avgCost)}</span>}
              sx={{ height: 26, borderRadius: '10px', fontWeight: 650 }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={<span><strong>Total:</strong> {fmt2(total)}</span>}
              sx={{ height: 26, borderRadius: '10px', fontWeight: 650 }}
            />
          </Stack>
        </Box>

        {exporting && (
          <Box sx={{ flexShrink: 0, px: 1, py: 0.75 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Querying locations… {exportDone}/{exportTotal}
            </Typography>
          </Box>
        )}

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            '& .MuiTableRow-hover:hover': {
              backgroundColor: 'rgba(26, 35, 126, 0.035)',
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 620,
                sm: 680,
                md: '100%',
              },
              width: '100%',
              tableLayout: {
                xs: 'fixed',
                md: 'auto',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 6,
                    bgcolor: '#f8fafc',
                    backgroundClip: 'padding-box',
                    width: {
                      xs: 210,
                      sm: 260,
                      md: '42%',
                    },
                    minWidth: {
                      xs: 210,
                      sm: 260,
                    },
                    boxShadow: {
                      xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                      md: 'none',
                    },
                  }}
                >
                  {sortLabel('location', 'Location')}
                </TableCell>

                {[
                  ['Produced', 'producedQty', { xs: 95, sm: 105, md: '14%' }],
                  ['Unit Cost', 'computedUnitCost', { xs: 95, sm: 110, md: '16%' }],
                  ['Total', 'totalProductionValue', { xs: 105, sm: 120, md: '16%' }],
                  ['Period', null, { xs: 90, sm: 95, md: '12%' }],
                ].map(([label, property, width]) => (
                  <TableCell key={label} sx={{ ...headerCellSx, width }} align="right">
                    {property ? sortLabel(property, label) : label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRows.map(r => {
                const key = `${r.location}|${ymKey(r.year, r.month)}`;
                const isSel = selectedKey === key;
                const val = Number(r.computedUnitCost);
                const diff = val - avgCost;

                return (
                  <TableRow
                    key={key}
                    hover
                    selected={!!isSel}
                    onClick={() => this.setSelected(r)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        bgcolor: isSel ? '#eef2ff' : '#fff',
                        backgroundClip: 'padding-box',
                        fontWeight: 650,
                        width: {
                          xs: 210,
                          sm: 260,
                          md: '42%',
                        },
                        minWidth: {
                          xs: 210,
                          sm: 260,
                        },
                        boxShadow: {
                          xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                          md: 'none',
                        },
                      }}
                      title={this.getLocationName(r.location)}
                    >
                      {this.getLocationName(r.location)}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {fmt0(r.producedQty)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        color: (theme) => {
                          if (!isFinite(val) || val === 0) return theme.palette.text.primary;
                          if (diff > 0.000001) return theme.palette.error.main;
                          if (diff < -0.000001) return theme.palette.success.main;
                          return theme.palette.text.primary;
                        },
                        fontWeight: 650,
                      }}
                    >
                      {fmt2(r.computedUnitCost)}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {fmt2(r.totalProductionValue)}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {ymKey(r.year, r.month)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!pageRows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={bodyCellSx}>
                    {this.state.productOpt ? 'No results' : 'Select a product'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, newPage) => this.setState({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            this.setState({ rowsPerPage: Number(e.target.value), page: 0 })
          }
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            minHeight: { xs: 44, sm: 52 },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              minHeight: { xs: 44, sm: 52 },
              px: { xs: 0.5, sm: 1 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.78rem', sm: '0.86rem' },
              m: 0,
            },
          }}
        />
      </Paper>
    );
  }

  renderSummaryChips(recipeUnitCost, recipeTotalValue, r) {
    const items = [
      { label: 'Qty', value: fmt0(r.producedQty) },
      { label: 'UCost', value: fmt2(r.computedUnitCost) },
      { label: 'Total', value: fmt2(r.totalProductionValue) },
      { label: 'R.UCost', value: fmt2(recipeUnitCost) },
      { label: 'R.Total', value: fmt2(recipeTotalValue) },
    ];
    return (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {items.map((it, idx) => (
          <Chip
            key={idx}
            size="small"
            variant="outlined"
            label={<span><strong>{it.label}:</strong> {it.value}</span>}
            sx={{ fontSize: 14, height: 24 }}
          />
        ))}
      </Stack>
    );
  }

  renderDetails() {
    const r = this.getSelectedRow();

    if (!r) {
      return (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            height: '100%',
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '14px',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Select a row to view details
          </Typography>
        </Paper>
      );
    }

    const recipeItems = this.state.recipeByProduct[String(r.finishedProduct)] || null;
    const { lines, recipeUnitCost } = this.buildComparison(r.breakdown || [], recipeItems || []);
    const recipeTotalValue = (Number(r.producedQty || 0) || 0) * (recipeUnitCost || 0);

    const sumCost = lines.reduce((acc, ln) => acc + (ln.lineValue || 0), 0);
    const sumRecipe = lines.reduce((acc, ln) => acc + (ln.recipeValue || 0), 0);

    const bodyCellSx = {
      py: { xs: 0.55, sm: 0.75, md: 0.8 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#111827',
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
      fontVariantNumeric: 'tabular-nums',
    };

    const headerCellSx = {
      py: { xs: 0.5, sm: 0.7, md: 0.85 },
      px: { xs: 0.45, sm: 0.75, md: 1 },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 700,
      lineHeight: 1.15,
      fontSize: {
        xs: '0.76rem',
        sm: '0.86rem',
        md: '0.92rem',
      },
    };

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.65, sm: 0.85 },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.84rem',
                    sm: '0.92rem',
                    md: '1rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={r.finishedProductName || '(unnamed)'}
              >
                {r.finishedProductName || '(unnamed)'}
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {this.getLocationName(r.location)} · {ymKey(r.year, r.month)}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small"
              variant="outlined"
              onClick={this.exportSelectedCsv}
              sx={{
                height: 32,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 750,
                flexShrink: 0,
              }}
            >
              Export
            </Button>
          </Stack>

          <Box sx={{ mt: 0.75 }}>
            {this.renderSummaryChips(recipeUnitCost, recipeTotalValue, r)}
          </Box>

          {!recipeItems && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                mt: 0.75,
                py: 0.25,
                borderRadius: '12px',
                fontSize: '0.78rem',
              }}
            >
              Recipe not loaded yet or not defined for this product.
            </Alert>
          )}
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 720,
                sm: 820,
                md: '100%',
              },
              width: '100%',
              tableLayout: {
                xs: 'fixed',
                md: 'auto',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 6,
                    bgcolor: '#f8fafc',
                    backgroundClip: 'padding-box',
                    width: {
                      xs: 220,
                      sm: 280,
                      md: '34%',
                    },
                    minWidth: {
                      xs: 220,
                      sm: 280,
                    },
                    boxShadow: {
                      xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                      md: 'none',
                    },
                  }}
                >
                  Ingredient
                </TableCell>

                {[
                  ['Cost Qty', { xs: 95, sm: 105, md: '13%' }],
                  ['Price', { xs: 90, sm: 100, md: '12%' }],
                  ['Total Cost', { xs: 105, sm: 120, md: '15%' }],
                  ['Rcp Qty', { xs: 95, sm: 105, md: '13%' }],
                  ['Rcp Val', { xs: 95, sm: 110, md: '13%' }],
                ].map(([label, width]) => (
                  <TableCell key={label} sx={{ ...headerCellSx, width }} align="right">
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {lines.map((ln, idx) => (
                <TableRow key={idx} hover>
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: '#fff',
                      backgroundClip: 'padding-box',
                      fontWeight: 600,
                      width: {
                        xs: 220,
                        sm: 280,
                        md: '34%',
                      },
                      minWidth: {
                        xs: 220,
                        sm: 280,
                      },
                      boxShadow: {
                        xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                        md: 'none',
                      },
                    }}
                    title={this.getProductName(ln.ingredient)}
                  >
                    {this.getProductName(ln.ingredient)}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>{fmt3(ln.costQty)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.priceUsedDisplay)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.lineValue)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt3(ln.recipeQty)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{fmt2(ln.recipeValue)}</TableCell>
                </TableRow>
              ))}

              {!lines.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info" variant="outlined">
                      No ingredients to show.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}

              {lines.length > 0 && (
                <TableRow
                  sx={{
                    bgcolor: '#fff',
                    '& .MuiTableCell-root': {
                      borderTop: '1px solid rgba(15, 23, 42, 0.12)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: '#fff',
                      backgroundClip: 'padding-box',
                      fontWeight: 800,
                      width: {
                        xs: 220,
                        sm: 280,
                        md: '34%',
                      },
                      minWidth: {
                        xs: 220,
                        sm: 280,
                      },
                      boxShadow: {
                        xs: '8px 0 10px -10px rgba(15, 23, 42, 0.45)',
                        md: 'none',
                      },
                    }}
                  >
                    Total
                  </TableCell>

                  <TableCell sx={bodyCellSx} />
                  <TableCell sx={bodyCellSx} />

                  <TableCell sx={{ ...bodyCellSx, fontWeight: 800 }} align="right">
                    {fmt2(sumCost)}
                  </TableCell>

                  <TableCell sx={bodyCellSx} />

                  <TableCell sx={{ ...bodyCellSx, fontWeight: 800 }} align="right">
                    {fmt2(sumRecipe)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderExportDialog() {
    const { exportOpen, rows } = this.state;
    const canExport = rows.length > 0;

    return (
      <Dialog open={exportOpen} onClose={() => this.setState({ exportOpen: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Export costs (by location)</DialogTitle>
        <DialogContent dividers>
          {!canExport && (
            <Alert severity="info" sx={{ mt: 1 }}>Nothing to export for this product.</Alert>
          )}
          {canExport && (
            <Alert severity="info" sx={{ mt: 1 }}>Exports the table on the left for the selected month & product.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ exportOpen: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { this.exportCostsCsvAllLocations(); this.setState({ exportOpen: false }); }}
            disabled={!canExport}
          >
            Export CSV
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: {
            xs: 720,
            sm: 760,
            md: 680,
            lg: 720,
          },
          maxHeight: {
            xs: 720,
            sm: 760,
            md: 680,
            lg: 720,
          },
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          p: {
            xs: 0.75,
            sm: 1,
            md: 1.25,
          },
          boxSizing: 'border-box',
        }}
      >
        {this.renderToolbar()}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 0.92fr) minmax(0, 1.08fr)',
            },
            gap: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
            {this.renderList()}
          </Box>

          <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
            {this.renderDetails()}
          </Box>
        </Box>

        {this.renderExportDialog()}

        <Dialog
          open={this.state.errorDialog.open}
          onClose={this.closeError}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Error</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {this.state.errorDialog.detail}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeError}>OK</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.state.toastOpen}
          autoHideDuration={2400}
          onClose={this.closeToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={this.closeToast}
            severity={this.state.toastSeverity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {this.state.toastMsg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default ProdCostsByLocation;
